import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { RecordInfoButton } from '../components/RecordInfoButton';
import { ImportExport, type ImportColumn } from '../components/ImportExport';
import { useToast } from '../components/Toast';
import type { BatchDto, DepartmentDto, Institution, ProgramDto, Student } from '../types/models';

const GENDERS = ['Male', 'Female', 'Other'];

// Semesters shown as Roman numerals I–VIII; the value stored/sent is the number 1–8.
const SEMESTERS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];
const toRoman = (n?: number | null) => (n && n >= 1 && n <= 8 ? SEMESTERS[n - 1] : '');

// Columns for the student import/export sheet. Batch is located by Ins/Dept/Prog/Batch codes.
// Batch Year Code is optional (cross-checked against the batch); Semester accepts I–VIII or 1–8.
const STUDENT_COLUMNS: ImportColumn[] = [
  { header: 'Institution Code', key: 'institutionCode', sample: 'DIT' },
  { header: 'Department Code', key: 'departmentCode', sample: 'CSE' },
  { header: 'Program Code', key: 'programCode', sample: 'BE-CSE' },
  { header: 'Batch Code', key: 'batchCode', sample: 'CSE-A' },
  { header: 'Batch Year Code', key: 'batchYearCode', sample: 'BY2024' },
  { header: 'Semester', key: 'semester', sample: 'I' },
  { header: 'Reg No', key: 'regNo', sample: '21CSE045' },
  { header: 'First Name', key: 'firstName', sample: 'Arjun' },
  { header: 'Last Name', key: 'lastName', sample: 'Kumar' },
  { header: "Father's Name", key: 'fatherName', sample: 'Ramesh Kumar' },
  { header: 'Email', key: 'email', sample: 'arjun.kumar@college.edu' },
  { header: 'Mobile', key: 'mobile', sample: '9876543210' },
  { header: 'Gender', key: 'gender', sample: 'Male' },
];
const emptyForm = {
  institutionId: '', departmentId: '', programId: '', batchId: '',
  regNo: '', firstName: '', lastName: '', fatherName: '', email: '', mobile: '', gender: '', semester: '',
};

export function StudentPage() {
  const toast = useToast();
  const { orgId = '' } = useParams();

  const [insts, setInsts] = useState<Institution[]>([]);
  const [depts, setDepts] = useState<DepartmentDto[]>([]);
  const [programs, setPrograms] = useState<ProgramDto[]>([]);
  const [batchOptions, setBatchOptions] = useState<BatchDto[]>([]);
  const [students, setStudents] = useState<Student[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Filter bar (Institution → Department → Program → Batch).
  const [filter, setFilter] = useState({ institutionId: '', departmentId: '', programId: '', batchId: '' });
  const [filterBatches, setFilterBatches] = useState<BatchDto[]>([]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Load batches for the program selected in the form.
  useEffect(() => {
    if (!form.programId) { setBatchOptions([]); return; }
    api.listBatches(form.programId).then(setBatchOptions).catch(() => setBatchOptions([]));
  }, [form.programId]);

  // Load batches for the program selected in the filter bar.
  useEffect(() => {
    if (!filter.programId) { setFilterBatches([]); return; }
    api.listBatches(filter.programId).then(setFilterBatches).catch(() => setFilterBatches([]));
  }, [filter.programId]);

  async function refresh() {
    try {
      const [i, d, p, s] = await Promise.all([
        api.listInstitutions(orgId), api.listDepartments(orgId), api.listPrograms(orgId), api.listStudents(orgId),
      ]);
      setInsts(i); setDepts(d); setPrograms(p); setStudents(s);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load students.');
    }
  }

  const deptsForForm = useMemo(
    () => depts.filter(d => d.institutionId === form.institutionId),
    [depts, form.institutionId],
  );
  const programsForForm = useMemo(
    () => programs.filter(p => p.departmentId === form.departmentId),
    [programs, form.departmentId],
  );

  const prerequisitesReady = insts.length > 0 && depts.length > 0 && programs.length > 0;

  // Filter-bar cascades.
  const deptsForFilter = useMemo(
    () => depts.filter(d => d.institutionId === filter.institutionId),
    [depts, filter.institutionId],
  );
  const programsForFilter = useMemo(
    () => programs.filter(p => p.departmentId === filter.departmentId),
    [programs, filter.departmentId],
  );

  function setFilterField(key: keyof typeof filter, value: string) {
    setFilter(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'institutionId') { next.departmentId = ''; next.programId = ''; next.batchId = ''; }
      if (key === 'departmentId') { next.programId = ''; next.batchId = ''; }
      if (key === 'programId') { next.batchId = ''; }
      return next;
    });
  }

  const filteredStudents = useMemo(
    () => (students ?? []).filter(s =>
      (!filter.institutionId || s.institutionId === filter.institutionId)
      && (!filter.departmentId || s.departmentId === filter.departmentId)
      && (!filter.programId || s.programId === filter.programId)
      && (!filter.batchId || s.batchId === filter.batchId)),
    [students, filter],
  );

  function openCreate() {
    setFormError(null); setEditId(null); setForm({ ...emptyForm }); setShowModal(true);
  }

  function openEdit(s: Student) {
    setFormError(null);
    setEditId(s.id);
    // The DTO carries the batch's program/department/institution, so the cascade prefills directly.
    setForm({
      institutionId: s.institutionId,
      departmentId: s.departmentId,
      programId: s.programId,
      batchId: s.batchId,
      regNo: s.regNo, firstName: s.firstName, lastName: s.lastName, fatherName: s.fatherName,
      email: s.email, mobile: s.mobile, gender: s.gender,
      semester: s.semester != null ? String(s.semester) : '',
    });
    setShowModal(true);
  }

  function setField(key: keyof typeof emptyForm, value: string) {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'institutionId') { next.departmentId = ''; next.programId = ''; next.batchId = ''; }
      if (key === 'departmentId') { next.programId = ''; next.batchId = ''; }
      if (key === 'programId') { next.batchId = ''; }
      return next;
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!/^\d{10}$/.test(form.mobile.trim())) {
      setFormError('Mobile number must be exactly 10 digits.');
      return;
    }
    setBusy(true);
    const payload = {
      batchId: form.batchId, regNo: form.regNo.trim(),
      firstName: form.firstName.trim(), lastName: form.lastName.trim(),
      fatherName: form.fatherName.trim(), email: form.email.trim(),
      mobile: form.mobile.trim(), gender: form.gender,
      semester: form.semester ? Number(form.semester) : null,
    };
    try {
      if (editId) await api.updateStudent(editId, payload);
      else await api.createStudent({ organizationId: orgId, ...payload });
      setShowModal(false);
      await refresh();
      toast.success(editId ? 'Student updated.' : 'Student created.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to save student.';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function remove(s: Student) {
    if (!confirm(`Delete student “${s.firstName} ${s.lastName}” (${s.regNo})? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.deleteStudent(s.id);
      await refresh();
      toast.success('Student deleted.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to delete student.';
      setError(msg);
      toast.error(msg);
    }
  }

  return (
    <OrgLayout>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Student</h1>
        <div className="row" style={{ gap: 8 }}>
          <ImportExport
            columns={STUDENT_COLUMNS}
            templateName="students-sample.xlsx"
            disabled={!prerequisitesReady}
            onImport={rows => api.importStudents(orgId, rows)}
            onDone={refresh}
            exportName="students"
            exportTitle="Students"
            exportRows={(filteredStudents ?? []).map((s, i) => ({
              'S.No': i + 1,
              'Reg No': s.regNo,
              'Name': `${s.firstName} ${s.lastName}`,
              'Batch': s.batchName,
              'Batch Year': s.batchYearCode ?? '',
              'Semester': toRoman(s.semester),
              'Email': s.email,
              'Mobile': s.mobile,
              'Gender': s.gender,
            }))}
          />
          <button className="primary" onClick={openCreate} disabled={!prerequisitesReady}>+ Add student</button>
        </div>
      </div>

      {!prerequisitesReady && students && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="muted">
            To add students you first need an{' '}
            <Link to={`/org/${orgId}/institutions`}>institution</Link>,{' '}
            <Link to={`/org/${orgId}/departments`}>department</Link>,{' '}
            <Link to={`/org/${orgId}/programs`}>program</Link>, and{' '}
            <Link to={`/org/${orgId}/batches`}>batch</Link>.
          </div>
        </div>
      )}

      {error && <div className="error" style={{ margin: '12px 0' }}>{error}</div>}

      <div className="card" style={{ marginTop: 16 }}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 180px' }}>
            <div className="muted">Institution</div>
            <select value={filter.institutionId} onChange={e => setFilterField('institutionId', e.target.value)}>
              <option value="">All</option>
              {insts.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 180px' }}>
            <div className="muted">Department</div>
            <select value={filter.departmentId} disabled={!filter.institutionId}
                    onChange={e => setFilterField('departmentId', e.target.value)}>
              <option value="">All</option>
              {deptsForFilter.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 180px' }}>
            <div className="muted">Program</div>
            <select value={filter.programId} disabled={!filter.departmentId}
                    onChange={e => setFilterField('programId', e.target.value)}>
              <option value="">All</option>
              {programsForFilter.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 180px' }}>
            <div className="muted">Batch</div>
            <select value={filter.batchId} disabled={!filter.programId}
                    onChange={e => setFilterField('batchId', e.target.value)}>
              <option value="">All</option>
              {filterBatches.map(b => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
            </select>
          </label>
          {(filter.institutionId || filter.departmentId || filter.programId || filter.batchId) && (
            <button onClick={() => setFilter({ institutionId: '', departmentId: '', programId: '', batchId: '' })}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>S.no</th>
              <th>Reg No</th>
              <th>Name</th>
              <th>Batch</th>
              <th>Batch Year</th>
              <th style={{ width: 60 }}>Sem</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Gender</th>
              <th style={{ width: 150 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!students && <tr><td colSpan={10} className="muted">Loading…</td></tr>}
            {students && filteredStudents.length === 0 && (
              <tr><td colSpan={10} className="muted">
                {students.length === 0 ? 'No students yet.' : 'No students match the selected filters.'}
              </td></tr>
            )}
            {filteredStudents.map((s, idx) => (
              <tr key={s.id}>
                <td>{idx + 1}</td>
                <td className="slug">{s.regNo}</td>
                <td style={{ fontWeight: 600 }}>{s.firstName} {s.lastName}</td>
                <td>{s.batchName}</td>
                <td>{s.batchYearCode ?? '—'}</td>
                <td>{toRoman(s.semester) || '—'}</td>
                <td>{s.email}</td>
                <td>{s.mobile}</td>
                <td>{s.gender}</td>
                <td>
                  <button className="link-btn" onClick={() => openEdit(s)}>Edit</button>
                  <button className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => remove(s)}>Delete</button>
                  <RecordInfoButton entity="students" id={s.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-backdrop" onMouseDown={() => setShowModal(false)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()}>
            <div className="modal-head">
              <h2>{editId ? 'Edit student' : 'Create student'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)} aria-label="Close">✕</button>
            </div>
            <form onSubmit={submit} className="col" style={{ marginTop: 16 }}>
              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: 1 }}>
                  <div className="muted">Institution</div>
                  <select value={form.institutionId} onChange={e => setField('institutionId', e.target.value)}>
                    <option value="" disabled>Select…</option>
                    {insts.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
                  </select>
                </label>
                <label style={{ flex: 1 }}>
                  <div className="muted">Department</div>
                  <select value={form.departmentId} disabled={!form.institutionId}
                          onChange={e => setField('departmentId', e.target.value)}>
                    <option value="" disabled>{form.institutionId ? 'Select…' : 'Pick institution first'}</option>
                    {deptsForForm.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
                  </select>
                </label>
              </div>
              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: 1 }}>
                  <div className="muted">Program</div>
                  <select value={form.programId} disabled={!form.departmentId}
                          onChange={e => setField('programId', e.target.value)}>
                    <option value="" disabled>{form.departmentId ? 'Select…' : 'Pick department first'}</option>
                    {programsForForm.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
                  </select>
                </label>
                <label style={{ flex: 1 }}>
                  <div className="muted">Batch *</div>
                  <select value={form.batchId} required disabled={!form.programId}
                          onChange={e => setField('batchId', e.target.value)}>
                    <option value="" disabled>{form.programId ? 'Select…' : 'Pick program first'}</option>
                    {batchOptions.map(b => <option key={b.id} value={b.id}>{b.code} — {b.name} ({b.academicYear})</option>)}
                  </select>
                </label>
                <label style={{ flex: 1 }}>
                  <div className="muted">Reg No *</div>
                  <input value={form.regNo} required maxLength={64}
                         onChange={e => setField('regNo', e.target.value)} placeholder="21CSE045" />
                </label>
              </div>
              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: 1 }}>
                  <div className="muted">First Name *</div>
                  <input value={form.firstName} required maxLength={120}
                         onChange={e => setField('firstName', e.target.value)} />
                </label>
                <label style={{ flex: 1 }}>
                  <div className="muted">Last Name *</div>
                  <input value={form.lastName} required maxLength={120}
                         onChange={e => setField('lastName', e.target.value)} />
                </label>
              </div>
              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: 1 }}>
                  <div className="muted">Father's Name *</div>
                  <input value={form.fatherName} required maxLength={200}
                         onChange={e => setField('fatherName', e.target.value)} />
                </label>
                <label style={{ flex: '0 0 160px' }}>
                  <div className="muted">Gender *</div>
                  <select value={form.gender} required onChange={e => setField('gender', e.target.value)}>
                    <option value="" disabled>Select…</option>
                    {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </label>
                <label style={{ flex: '0 0 140px' }}>
                  <div className="muted">Semester</div>
                  <select value={form.semester} onChange={e => setField('semester', e.target.value)}>
                    <option value="">— None —</option>
                    {SEMESTERS.map((r, i) => <option key={r} value={i + 1}>{r}</option>)}
                  </select>
                </label>
              </div>
              <div className="row" style={{ gap: 12 }}>
                <label style={{ flex: 1 }}>
                  <div className="muted">Email *</div>
                  <input type="email" value={form.email} required maxLength={320}
                         onChange={e => setField('email', e.target.value)} placeholder="student@college.edu" />
                </label>
                <label style={{ flex: 1 }}>
                  <div className="muted">Mobile No *</div>
                  <input value={form.mobile} required maxLength={10} inputMode="numeric" pattern="\d{10}"
                         onChange={e => setField('mobile', e.target.value.replace(/\D/g, ''))}
                         placeholder="9876543210" />
                </label>
              </div>
              {formError && <div className="error">{formError}</div>}
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="primary" disabled={busy}>
                  {busy ? 'Saving…' : editId ? 'Save changes' : 'Create student'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </OrgLayout>
  );
}
